import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import ConfirmModal from "../components/ConfirmModal";
import styles from "./TablePage.module.css";

type Word = {
  id: string;
  english: string;
  korean: string;
  category: string;
  example_en: string;
  example_ko: string;
  nuance_note: string;
  additional_note: string;
  related_words: string[];
  is_active: boolean;
};

const PRESET_CATS = ["general", "business", "slang"];

const EMPTY: Omit<Word, "id"> = {
  english: "",
  korean: "",
  category: "general",
  example_en: "",
  example_ko: "",
  nuance_note: "",
  additional_note: "",
  related_words: [],
  is_active: false,
};

export default function WordsPage() {
  const [rows, setRows] = useState<Word[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  const [pairMode, setPairMode] = useState(false);
  const [pairSelected, setPairSelected] = useState<string[]>([]);
  const [detailWord, setDetailWord] = useState<Word | null>(null);
  const [editing, setEditing] = useState<Word | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<any>({});
  const [customCat, setCustomCat] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Word | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("words").select("*").order("english");
    setRows(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  function openCreate() {
    setForm({ ...EMPTY });
    setCustomCat("");
    setCreating(true);
    setEditing(null);
  }
  function openEdit(row: Word) {
    setForm({ ...row, related_words: (row.related_words ?? []).join(", ") });
    setCustomCat(PRESET_CATS.includes(row.category) ? "" : row.category);
    setEditing(row);
    setCreating(false);
  }
  function closeModal() {
    setEditing(null);
    setCreating(false);
  }

  async function handleSave() {
    const payload = {
      ...form,
      related_words: Array.isArray(form.related_words)
        ? form.related_words
        : form.related_words
          ? form.related_words.split(",").map((s: string) => s.trim()).filter(Boolean)
          : [],
    };
    if (creating) {
      const { data, error } = await supabase
        .from("words")
        .insert([payload])
        .select();
      if (error) {
        alert(error.message);
        return;
      }
      setRows((prev) => [...prev, ...(data ?? [])]);
    } else if (editing) {
      const { data, error } = await supabase
        .from("words")
        .update(payload)
        .eq("id", editing.id)
        .select();
      if (error) {
        alert(error.message);
        return;
      }
      setRows((prev) =>
        prev.map((r) => (r.id === editing.id ? (data?.[0] ?? r) : r)),
      );
    }
    closeModal();
  }

  function togglePairSelect(id: string) {
    setPairSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length < 2 ? [...prev, id] : prev
    );
  }

  async function addPair() {
    if (pairSelected.length !== 2) return;
    const { error } = await supabase
      .from("word_pairs")
      .insert([{ word_a_id: pairSelected[0], word_b_id: pairSelected[1] }]);
    if (error) { alert(error.message); return; }
    setPairMode(false);
    setPairSelected([]);
  }

  async function handleDelete(row: Word) {
    const { error } = await supabase.from("words").delete().eq("id", row.id);
    if (error) {
      alert(error.message);
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    setDeleteTarget(null);
  }

  const isEtc = (cat: string) => !PRESET_CATS.includes(cat);

  const filtered = rows.filter((r) => {
    const matchSearch =
      !search ||
      `${r.english} ${r.korean} ${r.category}`
        .toLowerCase()
        .includes(search.toLowerCase());
    const matchCat =
      categoryFilter === "all" ||
      (categoryFilter === "etc" ? isEtc(r.category) : r.category === categoryFilter);
    const matchActive =
      activeFilter === "all" ||
      (activeFilter === "active" ? r.is_active : !r.is_active);
    return matchSearch && matchCat && matchActive;
  });

  const catCounts = {
    all: rows.length,
    general: rows.filter((r) => r.category === "general").length,
    business: rows.filter((r) => r.category === "business").length,
    slang: rows.filter((r) => r.category === "slang").length,
    etc: rows.filter((r) => isEtc(r.category)).length,
  };

  const isFormEtc = !PRESET_CATS.includes(form.category ?? "general");

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>📝 단어 관리</h1>
          <span className={styles.count}>{filtered.length}개</span>
          <div className={styles.dbBadge}>🗄 words</div>
        </div>
        <div className={styles.actions}>
          <input
            className={`input ${styles.search}`}
            placeholder="검색..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="btn btn-ghost" onClick={fetch}>
            ↺
          </button>
          <button className="btn btn-primary" onClick={openCreate}>
            + 추가
          </button>
        </div>
      </div>

      {/* Category + Active filter (한 줄) */}
      <div style={{ display: "flex", gap: 6, padding: "0 0 16px", flexWrap: "wrap", alignItems: "center" }}>
        {(["all", "general", "business", "slang", "etc"] as const).map((cat) => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            style={{
              padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700,
              border: "none", cursor: "pointer", transition: "all .15s",
              background: categoryFilter === cat ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.12)",
              color: categoryFilter === cat ? "#1a1a2e" : "rgba(255,255,255,0.6)",
            }}
          >
            {cat === "all" ? "전체" : cat}
            <span style={{ marginLeft: 5, opacity: 0.7, fontWeight: 400 }}>{catCounts[cat]}</span>
          </button>
        ))}

        {/* 구분선 */}
        <span style={{ width: 1, height: 18, background: "rgba(255,255,255,0.2)", margin: "0 4px", flexShrink: 0 }} />

        {(["all", "active", "inactive"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setActiveFilter(f)}
            title={f === "all" ? "전체" : f === "active" ? "활성" : "비활성"}
            style={{
              padding: "5px 10px", borderRadius: 999, fontSize: 13, fontWeight: 700,
              border: "none", cursor: "pointer", transition: "all .15s",
              background: activeFilter === f ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.12)",
              color: activeFilter === f ? "#1a1a2e" : "rgba(255,255,255,0.5)",
            }}
          >
            {f === "all" ? "◯" : f === "active" ? "✅" : "❌"}
          </button>
        ))}

        {/* 구분선 */}
        <span style={{ width: 1, height: 18, background: "rgba(255,255,255,0.2)", margin: "0 4px", flexShrink: 0 }} />

        {pairMode ? (
          <>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>
              {pairSelected.length}/2 선택
            </span>
            {pairSelected.length === 2 && (
              <button
                onClick={addPair}
                style={{
                  padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                  border: "none", cursor: "pointer",
                  background: "rgba(100,200,150,0.8)", color: "#fff",
                }}
              >
                ✓ A vs B 추가
              </button>
            )}
            <button
              onClick={() => { setPairMode(false); setPairSelected([]); }}
              style={{
                padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                border: "none", cursor: "pointer",
                background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)",
              }}
            >
              취소
            </button>
          </>
        ) : (
          <button
            onClick={() => { setPairMode(true); setPairSelected([]); }}
            style={{
              padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 700,
              border: "1px solid rgba(155,127,212,0.4)", cursor: "pointer",
              background: "rgba(155,127,212,0.15)", color: "rgba(200,170,255,0.9)",
            }}
          >
            🔀 A vs B 추가
          </button>
        )}
      </div>

      {loading ? (
        <div className={styles.loading}>로딩 중...</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>English</th>
                <th>Korean</th>
                <th>Category</th>
                <th>Example EN</th>
                <th>Example KO</th>
                <th>Nuance Note</th>
                <th>Additional</th>
                <th>Related Words</th>
                <th>Active</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setDetailWord(row)}
                  style={{ cursor: "pointer" }}
                >
                  <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={pairSelected.includes(row.id)}
                      disabled={!pairMode}
                      onChange={() => togglePairSelect(row.id)}
                      style={{
                        width: 16, height: 16,
                        cursor: pairMode ? "pointer" : "default",
                        accentColor: pairSelected.indexOf(row.id) === 0 ? "#9b7fd4" : "#e8649a",
                        opacity: pairMode ? 1 : 0.25,
                      }}
                    />
                  </td>
                  <td><strong>{row.english}</strong></td>
                  <td>{row.korean}</td>
                  <td>
                    <span style={{ background: "rgba(255,255,255,0.12)", borderRadius: 6, padding: "2px 8px", fontSize: 11 }}>
                      {row.category}
                    </span>
                  </td>
                  <td style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.example_en}</td>
                  <td style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.example_ko}</td>
                  <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.nuance_note}</td>
                  <td style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.additional_note}</td>
                  <td style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(row.related_words ?? []).join(", ")}</td>
                  <td style={{ textAlign: "center" }}>{row.is_active ? "✅" : "❌"}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className={styles.rowActions}>
                      <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => openEdit(row)}>편집</button>
                      <button className="btn btn-danger" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setDeleteTarget(row)}>삭제</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-box"
            style={{ width: 560, maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <h2 style={{ fontSize: 20, fontWeight: 800 }}>
                {creating ? "단어 추가" : "단어 편집"}
              </h2>
              <button
                className="btn btn-ghost"
                style={{ padding: "6px 10px" }}
                onClick={closeModal}
              >
                ✕
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { key: "english", label: "English *" },
                { key: "korean", label: "Korean *" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 1, display: "block", marginBottom: 6 }}>
                    {label}
                  </label>
                  <input
                    className="input"
                    value={form[key] ?? ""}
                    onChange={(e) => setForm((p: any) => ({ ...p, [key]: e.target.value }))}
                  />
                </div>
              ))}

              {/* Category selector */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 1, display: "block", marginBottom: 8 }}>
                  CATEGORY
                </label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[...PRESET_CATS, "etc"].map((cat) => {
                    const isActive = cat === "etc" ? isFormEtc : form.category === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => {
                          if (cat === "etc") {
                            setForm((p: any) => ({ ...p, category: customCat || "" }));
                          } else {
                            setCustomCat("");
                            setForm((p: any) => ({ ...p, category: cat }));
                          }
                        }}
                        style={{
                          padding: "5px 14px",
                          borderRadius: 999,
                          fontSize: 13,
                          fontWeight: 700,
                          border: "none",
                          cursor: "pointer",
                          background: isActive ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.12)",
                          color: isActive ? "#1a1a2e" : "rgba(255,255,255,0.6)",
                          transition: "all .15s",
                        }}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
                {isFormEtc && (
                  <input
                    className="input"
                    style={{ marginTop: 8 }}
                    placeholder="카테고리 직접 입력"
                    value={customCat}
                    autoFocus
                    onChange={(e) => {
                      setCustomCat(e.target.value);
                      setForm((p: any) => ({ ...p, category: e.target.value }));
                    }}
                  />
                )}
              </div>

              {/* Active toggle */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 1, display: "block", marginBottom: 8 }}>
                  ACTIVE
                </label>
                <div style={{ display: "flex", gap: 6 }}>
                  {[true, false].map((v) => (
                    <button
                      key={String(v)}
                      type="button"
                      onClick={() => setForm((p: any) => ({ ...p, is_active: v }))}
                      style={{
                        padding: "5px 16px", borderRadius: 999, fontSize: 13, fontWeight: 700,
                        border: "none", cursor: "pointer", transition: "all .15s",
                        background: form.is_active === v
                          ? v ? "rgba(100,200,150,0.8)" : "rgba(220,80,80,0.7)"
                          : "rgba(255,255,255,0.1)",
                        color: form.is_active === v ? "#fff" : "rgba(255,255,255,0.4)",
                      }}
                    >
                      {v ? "✅ active" : "❌ inactive"}
                    </button>
                  ))}
                </div>
              </div>

              {[
                { key: "example_en", label: "Example EN" },
                { key: "example_ko", label: "Example KO" },
                { key: "nuance_note", label: "Nuance Note — 실제로 어떤 뉘앙스인지" },
                { key: "additional_note", label: "Additional Explanation — 쉽게 풀어서 이해하기 좋은 설명" },
                { key: "related_words", label: "Related Words (쉼표로 구분)" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: 1, display: "block", marginBottom: 6 }}>
                    {label}
                  </label>
                  {key === "nuance_note" || key === "additional_note" || key === "example_en" || key === "example_ko" ? (
                    <textarea
                      className="input"
                      rows={2}
                      style={{ resize: "vertical" }}
                      value={form[key] ?? ""}
                      onChange={(e) => setForm((p: any) => ({ ...p, [key]: e.target.value }))}
                    />
                  ) : (
                    <input
                      className="input"
                      value={form[key] ?? ""}
                      onChange={(e) => setForm((p: any) => ({ ...p, [key]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              <button className="btn btn-ghost" onClick={closeModal}>
                취소
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {creating ? "추가" : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 우측 상세 패널 */}
      {detailWord && (
        <>
          <div
            onClick={() => setDetailWord(null)}
            style={{ position: "fixed", inset: 0, zIndex: 100 }}
          />
          <div style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: 400,
            background: "rgba(12, 6, 30, 0.97)",
            backdropFilter: "blur(24px)",
            borderLeft: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "-8px 0 40px rgba(0,0,0,0.5)",
            zIndex: 101, overflowY: "auto",
            display: "flex", flexDirection: "column",
          }}>
            {/* 헤더 */}
            <div style={{ padding: "24px 24px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "white", letterSpacing: -0.5 }}>{detailWord.english}</div>
                  <div style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{detailWord.korean}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                    <span style={{ background: "rgba(255,255,255,0.1)", borderRadius: 6, padding: "2px 10px", fontSize: 11, color: "rgba(255,255,255,0.6)" }}>{detailWord.category}</span>
                    <span style={{ background: detailWord.is_active ? "rgba(100,200,150,0.2)" : "rgba(220,80,80,0.2)", borderRadius: 6, padding: "2px 10px", fontSize: 11, color: detailWord.is_active ? "#64C896" : "#f08080" }}>
                      {detailWord.is_active ? "✅ active" : "❌ inactive"}
                    </span>
                  </div>
                </div>
                <button onClick={() => setDetailWord(null)} style={{ color: "rgba(255,255,255,0.4)", background: "none", border: "none", fontSize: 18, cursor: "pointer", padding: 4 }}>✕</button>
              </div>
            </div>

            {/* 내용 */}
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20, flex: 1 }}>
              {[
                { label: "Example EN", value: detailWord.example_en },
                { label: "Example KO", value: detailWord.example_ko },
                { label: "Nuance Note", value: detailWord.nuance_note },
                { label: "Additional Explanation", value: detailWord.additional_note },
                { label: "Related Words", value: (detailWord.related_words ?? []).join(", ") },
              ].filter(f => f.value).map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.35)", letterSpacing: 1.2, marginBottom: 6, textTransform: "uppercase" }}>{label}</div>
                  <div style={{ fontSize: 14, color: "rgba(255,255,255,0.85)", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{value}</div>
                </div>
              ))}
            </div>

            {/* 액션 */}
            <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", gap: 8 }}>
              <button
                className="btn btn-ghost"
                style={{ flex: 1, justifyContent: "center" }}
                onClick={() => { openEdit(detailWord); setDetailWord(null); }}
              >
                편집
              </button>
              <button
                className="btn btn-danger"
                style={{ flex: 1, justifyContent: "center" }}
                onClick={() => { setDeleteTarget(detailWord); setDetailWord(null); }}
              >
                삭제
              </button>
            </div>
          </div>
        </>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="단어 삭제"
          message={`"${deleteTarget.english}"를 삭제할까요?`}
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
